import { Link, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  useCollections,
  useCurrentWorkspace,
  useDocuments,
  useViewer,
  useWizardCapabilities,
} from "~/api/queries";
import { DocumentList } from "~/features/documents/DocumentList";
import { isWizardDismissed, shouldShowWizard } from "~/features/welcome/wizardState";
import { WorkspaceDecisions } from "~/features/work/WorkspaceDecisions";
import { Text } from "~/foundation/components/Text";
import { useDocumentTitle } from "~/foundation/hooks/useDocumentTitle";
import { cn } from "~/foundation/utils/cn";

export type HomeTab = "recent" | "mine" | "drafts";

const TABS: Array<{ id: HomeTab; label: string }> = [
  { id: "recent", label: "Recently updated" },
  { id: "mine", label: "Created by me" },
  { id: "drafts", label: "Drafts" },
];

export function HomeScene({ tab }: { tab: HomeTab }) {
  const { t } = useTranslation();
  const { data: viewer } = useViewer();
  const { data: collections } = useCollections();
  const { data: documents, isLoading } = useDocuments({
    status: tab === "drafts" ? "draft" : "published",
  });
  useDocumentTitle(t("Home"));

  /**
   * W4 §E-9 — the first-run wizard is a ROUTE, and this is the branch that sends a brand-new
   * workspace to it. kms replaced Home's BODY with the wizard, which coupled onboarding to
   * Home's data loading and made the whole thing unreachable once dismissed; a redirect keeps
   * `/welcome` linkable, re-enterable and skippable, and leaves Home to be Home.
   *
   * The predicate lives in `wizardState.ts` with its own tests — in particular the step-aside
   * exemption, which is what stops the collection created in step 1 evicting the wizard at
   * step 2.
   */
  const navigate = useNavigate();
  const { data: workspace } = useCurrentWorkspace();
  const { data: wizardCapabilities } = useWizardCapabilities();
  const showWizard = shouldShowWizard({
    capable: wizardCapabilities?.wizard,
    collectionsLoaded: collections !== undefined,
    activeCollectionIds: (collections ?? []).map((collection) => collection.id),
    createdCollectionId: null,
    dismissed: isWizardDismissed(workspace?.id),
    finished: false,
  });

  useEffect(() => {
    if (showWizard) {
      void navigate({ to: "/welcome" });
    }
  }, [showWizard, navigate]);

  const visible =
    tab === "mine" ? (documents ?? []).filter((d) => d.createdBy.id === viewer?.id) : documents;

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 p-6">
      <Text as="h1" variant="h1">
        {t("Welcome back, {{name}}", { name: viewer?.name ?? "" })}
      </Text>

      <div className="rounded-lg border border-divider p-4">
        <WorkspaceDecisions limit={3} />
      </div>
      {collections?.length ? (
        <section className="flex flex-col gap-2">
          <Text variant="small" weight="medium" className="uppercase tracking-wide">
            {t("Collections")}
          </Text>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            {collections.map((collection) => (
              <Link
                key={collection.id}
                to="/collections/$collectionId"
                params={{ collectionId: collection.id }}
                className="flex items-center gap-2 rounded-md border border-divider px-3 py-2.5 hover:bg-hover-bg"
              >
                <span aria-hidden>{collection.emoji ?? "📁"}</span>
                <span className="truncate text-body text-text">{collection.name}</span>
              </Link>
            ))}
          </div>
        </section>
      ) : null}

      <section className="flex flex-col gap-2">
        <div role="tablist" aria-label={t("Home")} className="flex gap-1 border-b border-divider">
          {TABS.map((entry) => (
            <Link
              key={entry.id}
              to="/"
              search={{ tab: entry.id }}
              role="tab"
              aria-selected={tab === entry.id}
              className={cn(
                "-mb-px border-b-2 px-3 py-2 text-body",
                tab === entry.id
                  ? "border-accent font-medium text-text"
                  : "border-transparent text-text-secondary hover:text-text",
              )}
            >
              {t(entry.label)}
            </Link>
          ))}
        </div>

        <DocumentList
          documents={visible}
          isLoading={isLoading}
          emptyMessage={t("Nothing here yet")}
        />
      </section>
    </div>
  );
}
